import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import heic2any from "heic2any";
import "./DisplayUploads.css";
import { listBooksWithImages } from "../../graphql/custom-queries";
import { deleteBook } from "../../graphql/mutations";
import { generateClient } from "aws-amplify/api";
import { getUrl } from "aws-amplify/storage";

interface Image {
  id: string;
  key: string;
  url?: string;
  type?: string;
}

interface BookDetails {
  id: string;
  title: string;
  authorName: string;
  createdAt: string;
  images?: {
    items: Image[];
  };
}

interface DisplayUploadsProps {
  searchQuery: string;
}

const client = generateClient();

const DisplayUploads: React.FC<DisplayUploadsProps> = ({ searchQuery }) => {
  const [books, setBooks] = useState<BookDetails[]>([]);
  const [selectedBook, setSelectedBook] = useState<BookDetails | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filteredBooks, setFilteredBooks] = useState<BookDetails[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const booksResponse = await client.graphql({
          query: listBooksWithImages,
        });

        const rawBooks = (booksResponse as any)?.data?.listBooks?.items ?? [];

        const fetchedBooks = await Promise.all(
          rawBooks.map(async (book: BookDetails) => {
            const imageItems = book.images?.items ?? [];

            const imagesWithUrls = await Promise.all(
              imageItems.map(async (image: Image) => {
                const { url } = await getUrl({
                  key: image.key,
                  options: { accessLevel: "guest" },
                });

                let convertedUrl = url.toString();
                if (image.type === "image/heic") {
                  const response = await fetch(convertedUrl);
                  const blob = await response.blob();
                  const convertedBlob = await heic2any({
                    blob,
                    toType: "image/jpeg",
                  });
                  convertedUrl = Array.isArray(convertedBlob)
                    ? URL.createObjectURL(convertedBlob[0])
                    : URL.createObjectURL(convertedBlob as Blob);
                }

                return { ...image, url: convertedUrl };
              })
            );

            return {
              ...book,
              images: { items: imagesWithUrls },
            };
          })
        );

        fetchedBooks.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setBooks(fetchedBooks);
      } catch (error) {
        console.error("Error fetching files:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchFiles();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const filtered = books.filter(
        (book) =>
          book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          book.authorName.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredBooks(filtered);
    } else {
      setFilteredBooks(books);
    }
  }, [searchQuery, books]);

  const handleClickOpen = (book: BookDetails) => {
    setSelectedBook(book);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedBook(null);
  };

  const handleDelete = async () => {
    if (!selectedBook) return;
    try {
      await client.graphql({
        query: deleteBook,
        variables: { input: { id: selectedBook.id } },
      });
      setBooks((prev) => prev.filter((b) => b.id !== selectedBook.id));
      setFilteredBooks((prev) => prev.filter((b) => b.id !== selectedBook.id));
      setDeleteConfirmOpen(false);
      setOpen(false);
      setSelectedBook(null);
    } catch (error) {
      console.error("Error deleting book:", error);
    }
  };

  return (
    <div className="display-books">
      {loading ? (
        <div className="loading-indicator">Loading books...</div>
      ) : filteredBooks.length === 0 ? (
        <div className="no-books">No books yet!</div>
      ) : (
        filteredBooks.map((book) => {
          const imageItems = book.images?.items ?? [];
          const hasImages = imageItems.length > 0;

          return (
            <div className="display-thumbnail" key={book.id}>
              {hasImages ? (
                <img
                  key={imageItems[0].id}
                  className="book-thumbnail"
                  src={imageItems[0].url}
                  alt={`Thumbnail for ${book.title}`}
                  onClick={() => handleClickOpen(book)}
                  style={{ cursor: "pointer" }}
                />
              ) : (
                <div
                  className="no-thumbnail"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleClickOpen(book)}
                >
                  No image
                </div>
              )}
              <span className="book-title">{book.title}</span>
              <span className="book-author">{book.authorName}</span>
            </div>
          );
        })
      )}

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingRight: 2,
          }}
        >
          <span>{selectedBook?.title}</span>
          <div>
            <Tooltip title="Delete Book">
              <IconButton
                onClick={() => setDeleteConfirmOpen(true)}
                sx={{ color: "red", marginRight: 1 }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Tooltip>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{ color: (theme) => theme.palette.grey[500] }}
            >
              <CloseIcon />
            </IconButton>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          {selectedBook?.images?.items.length ? (
            <Grid container spacing={2} direction="column">
              {selectedBook.images.items.map((image, index) => (
                <Grid item xs={12} key={image.id}>
                  <div className="image-container">
                    <img
                      src={image.url}
                      alt={`image-${image.id}`}
                      style={{ width: "100%", height: "auto" }}
                    />
                    <div className="page-number">Page {index + 1}</div>
                  </div>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography>No pages to display.</Typography>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Delete Book</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this book?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DisplayUploads;
